import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 5198:1787 — Sidebar 01):
 *
 * Sidebar:         w-[256px], bg-muted/50 (#fafafa), flex flex-col, overflow-clip
 * SidebarHeader:   bg-muted/50, p-2, gap-2, flex flex-col
 * SidebarContent:  flex-1, overflow-y-auto, overflow-x-clip
 * SidebarFooter:   p-2, flex flex-col, gap-2
 * SidebarGroup:    p-2, flex flex-col
 * SidebarGroupLabel: h-8, px-2, rounded-md, text-xs font-medium, opacity-70, text-foreground
 * SidebarMenu:     flex flex-col, cursor-pointer
 * SidebarMenuItem: flex flex-col
 * SidebarMenuButton: h-8, p-2, gap-2, rounded-md, text-sm, leading-none
 *   Default: text-foreground, font-normal
 *   Active:  bg-accent, text-accent-foreground, font-medium
 *   Hover:   hover:bg-accent, hover:text-accent-foreground
 */

/* ─── Sidebar Root ─── */

const IntuiSidebar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <aside
    ref={ref}
    className={cn(
      'flex h-full w-[256px] flex-col overflow-clip bg-muted/50',
      className,
    )}
    {...props}
  />
))
IntuiSidebar.displayName = 'IntuiSidebar'

/* ─── SidebarHeader ─── */

const IntuiSidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-2 bg-muted/50 p-2', className)}
    {...props}
  />
))
IntuiSidebarHeader.displayName = 'IntuiSidebarHeader'

/* ─── SidebarContent ─── */

const IntuiSidebarContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-clip', className)}
    {...props}
  />
))
IntuiSidebarContent.displayName = 'IntuiSidebarContent'

/* ─── SidebarFooter ─── */

const IntuiSidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-2 p-2', className)}
    {...props}
  />
))
IntuiSidebarFooter.displayName = 'IntuiSidebarFooter'

/* ─── SidebarGroup ─── */

const IntuiSidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col p-2', className)}
    {...props}
  />
))
IntuiSidebarGroup.displayName = 'IntuiSidebarGroup'

/* ─── SidebarGroupLabel ─── */

const IntuiSidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex h-8 items-center justify-between rounded-md px-2',
      'text-xs font-medium text-muted-foreground',
      className,
    )}
    {...props}
  />
))
IntuiSidebarGroupLabel.displayName = 'IntuiSidebarGroupLabel'

/* ─── SidebarMenu ─── */

const IntuiSidebarMenu = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col', className)}
    {...props}
  />
))
IntuiSidebarMenu.displayName = 'IntuiSidebarMenu'

/* ─── SidebarMenuItem ─── */

const IntuiSidebarMenuItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col', className)}
    {...props}
  />
))
IntuiSidebarMenuItem.displayName = 'IntuiSidebarMenuItem'

/* ─── SidebarMenuButton ─── */

export interface IntuiSidebarMenuButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Whether this item is currently active/selected */
  isActive?: boolean
  /** Optional icon rendered before the label */
  icon?: React.ReactNode
  /** Optional badge or action rendered after the label */
  badge?: React.ReactNode
}

const IntuiSidebarMenuButton = React.forwardRef<
  HTMLButtonElement,
  IntuiSidebarMenuButtonProps
>(({ className, isActive = false, icon, badge, children, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    data-active={isActive || undefined}
    className={cn(
      'flex h-8 w-full items-center gap-2 rounded-md p-2 text-sm leading-none text-left',
      'text-foreground',
      'hover:bg-accent hover:text-accent-foreground',
      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
      isActive && 'bg-accent text-accent-foreground font-medium',
      className,
    )}
    {...props}
  >
    {icon && <span className="flex shrink-0 items-center justify-center [&>svg]:size-4">{icon}</span>}
    <span className="flex-1 truncate">{children}</span>
    {badge && <span className="shrink-0">{badge}</span>}
  </button>
))
IntuiSidebarMenuButton.displayName = 'IntuiSidebarMenuButton'

/* ─── SidebarHeaderButton (logo + title + version + collapse) ─── */

export interface IntuiSidebarHeaderButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Logo icon */
  icon?: React.ReactNode
  /** App/section title */
  title: string
  /** Version or subtitle text */
  subtitle?: string
  /** Collapse icon (defaults to ChevronsUpDown style) */
  collapseIcon?: React.ReactNode
}

const IntuiSidebarHeaderButton = React.forwardRef<
  HTMLButtonElement,
  IntuiSidebarHeaderButtonProps
>(({ className, icon, title, subtitle, collapseIcon, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(
      'flex w-full items-center gap-2 rounded-md p-2',
      'hover:bg-accent hover:text-accent-foreground',
      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
      className,
    )}
    {...props}
  >
    {icon && (
      <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground [&>svg]:size-4">
        {icon}
      </span>
    )}
    <span className="flex flex-1 flex-col gap-0.5 leading-none text-foreground overflow-hidden">
      <span className="truncate text-sm font-semibold">{title}</span>
      {subtitle && <span className="truncate text-xs">{subtitle}</span>}
    </span>
    {collapseIcon && (
      <span className="flex size-4 shrink-0 items-center justify-center text-foreground">
        {collapseIcon}
      </span>
    )}
  </button>
))
IntuiSidebarHeaderButton.displayName = 'IntuiSidebarHeaderButton'

/* ─── SidebarSeparator ─── */

const IntuiSidebarSeparator = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('mx-2 h-px bg-border', className)}
    {...props}
  />
))
IntuiSidebarSeparator.displayName = 'IntuiSidebarSeparator'

export {
  IntuiSidebar,
  IntuiSidebarHeader,
  IntuiSidebarContent,
  IntuiSidebarFooter,
  IntuiSidebarGroup,
  IntuiSidebarGroupLabel,
  IntuiSidebarMenu,
  IntuiSidebarMenuItem,
  IntuiSidebarMenuButton,
  IntuiSidebarHeaderButton,
  IntuiSidebarSeparator,
}
